import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Box,
    Button,
    Container,
    Typography,
    Grid,
    Fab,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PsychologyIcon from '@mui/icons-material/Psychology';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PublicIcon from '@mui/icons-material/Public';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { keyframes } from '@mui/system';
import { useHasActiveAccess, SubscriberCtaContent } from '../components/AdaptiveCta';
import { useAuth } from '../contexts/AuthContext';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const cyan = '#00BFFF';
const green = '#8DC63F';
const cardBg = '#141E30';
const borderColor = 'rgba(0, 191, 255, 0.12)';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const GlassCard = ({ children, sx = {}, ...props }) => (
    <Box
        sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha(cardBg, 0.7) : 'background.paper',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            backdropFilter: 'blur(12px)',
            p: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
                borderColor: alpha(cyan, 0.3),
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 40px ${alpha('#000', 0.4)}`,
            },
            ...sx,
        }}
        {...props}
    >
        {children}
    </Box>
);

const ScrollToTopButton = () => {
    const [visible, setVisible] = React.useState(false);
    const [fade, setFade] = React.useState(false);

    React.useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 200) setVisible(true);
            else setVisible(false);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleClick = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setFade(true);
        setTimeout(() => { setVisible(false); setFade(false); }, 500);
    };

    if (!visible) return null;

    return (
        <Box onClick={handleClick} sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000, opacity: fade ? 0 : 1, transition: 'opacity 0.5s ease-out' }}>
            <Fab color="primary" aria-label="scroll back to top">
                <KeyboardArrowUpIcon />
            </Fab>
        </Box>
    );
};

const values = [
    {
        icon: <PsychologyIcon sx={{ fontSize: 36 }} />,
        title: 'Comprehension First',
        desc: 'Speed is useless if you forget everything. Every tool we build starts with the question: does this help the reader understand more, not just read faster?',
        color: cyan,
    },
    {
        icon: <VisibilityIcon sx={{ fontSize: 36 }} />,
        title: 'Give Attention a Path',
        desc: 'Focus is not about forcing stillness. It is about giving your eyes and mind something to follow. The Guide Dot exists because rhythm beats willpower.',
        color: green,
    },
    {
        icon: <MenuBookIcon sx={{ fontSize: 36 }} />,
        title: 'Reading Should Feel Good',
        desc: 'Reading is not a chore to optimize. It is one of the most rewarding things a human can do. We build tools that make reading feel freer, not more frantic.',
        color: cyan,
    },
    {
        icon: <SpeedIcon sx={{ fontSize: 36 }} />,
        title: 'Train the Habit, Not the Reader',
        desc: 'Slow reading is not a character flaw. It is a set of fixable habits. We help readers identify what slows them down and practice their way past it.',
        color: green,
    },
    {
        icon: <PublicIcon sx={{ fontSize: 36 }} />,
        title: 'Reading Is for Everyone',
        desc: '16 languages. 14 reading levels. From Grade 1 to native fluency. We built ReaderFlex to work for readers at every stage, in every place.',
        color: cyan,
    },
    {
        icon: <FavoriteIcon sx={{ fontSize: 36 }} />,
        title: 'No Hype, No Tricks',
        desc: 'No "read 10x faster instantly." No fake research. No unrealistic promises. Just practical tools that help real readers build real rhythm.',
        color: green,
    },
];

const stats = [
    { value: '16', label: 'Languages Supported', color: cyan },
    { value: '94', label: 'Badges to Unlock', color: green },
    { value: '14', label: 'Reading Levels', color: cyan },
    { value: '100+', label: 'Free Reading Guides', color: green },
];

const milestones = [
    {
        title: 'The Problem',
        body: 'Reading is one of the most valuable skills a person can build. But most readers were never taught how to read well. They were taught to decode words, not to move through text with rhythm, focus, and confidence.',
    },
    {
        title: 'The Insight',
        body: 'Reading speed is a trainable skill. The habits that slow readers down (rereading, subvocalizing, drifting, losing place) are not intelligence problems. They are rhythm problems. And rhythm can be trained.',
    },
    {
        title: 'The Tool',
        body: 'ReaderFlex started with one idea: give readers a visible path. The Guide Dot was the first feature. Speed controls, comprehension quizzes, vocabulary tracking, and gamified progress followed. Every feature exists to help readers practice deliberately.',
    },
    {
        title: 'The Mission',
        body: 'Help as many people as possible discover the joy of reading. Not by rushing them through books. By giving them the tools to read with confidence, focus, and retention. So they can read more, remember more, and actually enjoy the time they spend with a page.',
    },
];

const About = () => {
    const navigate = useNavigate();
    const hasActiveAccess = useHasActiveAccess();
    const { user } = useAuth();

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: 'About ReaderFlex',
        description: 'ReaderFlex is a reading-training platform built around comprehension-first speed. Our mission is to help as many people as possible discover the joy of reading.',
        url: 'https://readerflex.com/about',
        publisher: {
            '@type': 'Organization',
            name: 'ReaderFlex',
            url: 'https://readerflex.com',
            founder: { '@type': 'Organization', name: 'Emberline Ventures, LLC' },
        },
    };

    return (
        <>
            <Helmet>
                <title>About ReaderFlex | Our Mission to Help You Read Better</title>
                <meta name="description" content="ReaderFlex is a reading-training platform built around comprehension-first speed. Learn about our mission to help readers everywhere discover the joy of reading with confidence, focus, and retention." />
                <link rel="canonical" href="https://readerflex.com/about" />
                <meta property="og:title" content="About ReaderFlex" />
                <meta property="og:description" content="ReaderFlex is a reading-training platform built around comprehension-first speed. Our mission is to help as many people as possible discover the joy of reading." />
                <meta property="og:url" content="https://readerflex.com/about" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="About ReaderFlex" />
                <meta name="twitter:description" content="ReaderFlex is a reading-training platform built around comprehension-first speed. Our mission is to help as many people as possible discover the joy of reading." />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            <Box sx={{ overflow: 'hidden' }}>
                {/* Hero */}
                <Box
                    sx={{
                        position: 'relative',
                        minHeight: { xs: '70vh', md: '80vh' },
                        display: 'flex',
                        alignItems: 'center',
                        pt: { xs: 8, md: 0 },
                        pb: { xs: 6, md: 0 },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            background: `radial-gradient(ellipse at 30% 20%, ${alpha(cyan, 0.08)} 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, ${alpha(green, 0.06)} 0%, transparent 60%)`,
                            pointerEvents: 'none',
                        },
                    }}
                >
                    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                        <Box sx={{ animation: `${fadeIn} 0.8s ease-out`, textAlign: 'center' }}>
                            <Typography
                                variant="overline"
                                sx={{ color: cyan, letterSpacing: '0.12em', fontSize: '0.8rem', display: 'block', mb: 2 }}
                            >
                                About ReaderFlex
                            </Typography>
                            <Typography
                                component="h1"
                                variant="h1"
                                sx={{
                                    fontWeight: 800,
                                    mb: 3,
                                    fontSize: { xs: '2.2rem', md: '3.5rem' },
                                    lineHeight: 1.15,
                                    color: 'text.primary',
                                }}
                            >
                                We believe reading is{' '}
                                <Box component="span" sx={{ color: cyan }}>the most valuable skill</Box>{' '}
                                a person can build.
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{ mb: 4, color: 'text.secondary', fontWeight: 400, lineHeight: 1.6, maxWidth: '680px', mx: 'auto' }}
                            >
                                ReaderFlex is a reading-training platform built around comprehension-first speed. We help readers practice controlled speed, build reading rhythm, improve focus, and remember more of what they read. No hype. No reckless skimming. Just practical tools that work.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                {hasActiveAccess ? (
                                    <>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="large"
                                            onClick={() => {
                                                const link = `${window.location.origin}?ref=${user?.username || ''}`;
                                                navigator.clipboard.writeText(link);
                                            }}
                                            startIcon={<ContentCopyIcon />}
                                            sx={{ py: 1.5, px: 5, fontSize: '1.05rem' }}
                                        >
                                            Copy Affiliate Link
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="large"
                                            onClick={() => navigate('/affiliate')}
                                            sx={{ py: 1.5, px: 5 }}
                                        >
                                            Affiliate Dashboard
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="large"
                                            onClick={() => navigate('/register')}
                                            sx={{ py: 1.5, px: 5, fontSize: '1.05rem' }}
                                        >
                                            Start Your Trial
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="large"
                                            onClick={() => navigate('/features')}
                                            sx={{ py: 1.5, px: 5 }}
                                        >
                                            See the Features
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Container>
                </Box>

                {/* Stats Bar */}
                <Box sx={{ py: { xs: 6, md: 8 }, borderTop: `1px solid ${borderColor}`, borderBottom: `1px solid ${borderColor}` }}>
                    <Container maxWidth="lg">
                        <Grid container spacing={4} justifyContent="center">
                            {stats.map((stat, i) => (
                                <Grid item xs={6} md={3} key={i}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h2" sx={{ fontWeight: 800, color: stat.color, mb: 0.5 }}>{stat.value}</Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{stat.label}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>

                {/* Our Story / Milestones */}
                <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 } }}>
                    <Typography variant="h2" align="center" sx={{ mb: 2, color: 'text.primary', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                        How ReaderFlex{' '}
                        <Box component="span" sx={{ color: green }}>Started</Box>
                    </Typography>
                    <Typography variant="h6" align="center" sx={{ mb: 8, color: 'text.secondary', maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
                        Reading faster is not about becoming a machine. It is about building a better rhythm with your eyes, attention, and memory.
                    </Typography>
                    {milestones.map((m, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: 'flex',
                                gap: { xs: 2, md: 4 },
                                mb: 5,
                                animation: `${fadeIn} 0.6s ease ${i * 0.1}s both`,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        backgroundColor: alpha(i % 2 === 0 ? cyan : green, 0.12),
                                        border: `2px solid ${alpha(i % 2 === 0 ? cyan : green, 0.3)}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: i % 2 === 0 ? cyan : green,
                                        fontWeight: 800,
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    {i + 1}
                                </Box>
                                {i < milestones.length - 1 && (
                                    <Box sx={{ width: 2, flex: 1, backgroundColor: alpha(i % 2 === 0 ? cyan : green, 0.15), mt: 1 }} />
                                )}
                            </Box>
                            <Box sx={{ pb: 2 }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                                    {m.title}
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, fontSize: '1.05rem' }}>
                                    {m.body}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Container>

                {/* What We Believe / Values */}
                <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: (theme) => alpha(theme.palette.mode === 'dark' ? '#000' : '#f5f5f5', 0.3) }}>
                    <Container maxWidth="lg">
                        <Typography variant="h2" align="center" sx={{ mb: 2, color: 'text.primary', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                            What We{' '}
                            <Box component="span" sx={{ color: cyan }}>Believe</Box>
                        </Typography>
                        <Typography variant="h6" align="center" sx={{ mb: 8, color: 'text.secondary', maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
                            Six principles that shape every feature, every blog post, and every email we send.
                        </Typography>
                        <Grid container spacing={3}>
                            {values.map((v, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <GlassCard sx={{ height: '100%' }}>
                                        <Box sx={{ width: 52, height: 52, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: alpha(v.color, 0.12), color: v.color, mb: 2 }}>
                                            {v.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>{v.title}</Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{v.desc}</Typography>
                                    </GlassCard>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>

                {/* What We Built */}
                <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                    <Typography variant="h2" align="center" sx={{ mb: 2, color: 'text.primary', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                        What We{' '}
                        <Box component="span" sx={{ color: green }}>Built</Box>
                    </Typography>
                    <Typography variant="h6" align="center" sx={{ mb: 8, color: 'text.secondary', maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
                        Tools that help readers practice deliberately, track progress, and stay motivated.
                    </Typography>
                    <Grid container spacing={3}>
                        {[
                            { icon: <SpeedIcon sx={{ fontSize: 36 }} />, title: 'Guided Reading Tools', desc: 'The Guide Dot gives your attention a visible path. Speed controls help you train controlled speed instead of reckless rushing.', color: cyan },
                            { icon: <PsychologyIcon sx={{ fontSize: 36 }} />, title: 'Comprehension Practice', desc: 'Every article comes with quizzes to verify retention. Speed only matters if understanding comes with it.', color: green },
                            { icon: <AutoStoriesIcon sx={{ fontSize: 36 }} />, title: 'AI-Generated Articles', desc: 'Fresh practice material on any topic, matched to your reading level. 14 difficulty levels across 16 languages.', color: cyan },
                            { icon: <TrendingUpIcon sx={{ fontSize: 36 }} />, title: 'Progress Tracking', desc: 'WPM, comprehension scores, streaks, XP, and weekly trends. See your improvement at a glance.', color: green },
                            { icon: <MenuBookIcon sx={{ fontSize: 36 }} />, title: 'Vocabulary Building', desc: 'Save words as you read. Each word gets pronunciation, definition, and spaced-repetition flashcards.', color: cyan },
                            { icon: <FavoriteIcon sx={{ fontSize: 36 }} />, title: 'Gamified Motivation', desc: '94 badges across 17 categories. Dual leaderboards. Reader Spotlight. Reading improvement should feel rewarding, not like homework.', color: green },
                        ].map((f, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <GlassCard sx={{ height: '100%' }}>
                                    <Box sx={{ width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: alpha(f.color, 0.12), color: f.color, mb: 2 }}>
                                        {f.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>{f.title}</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{f.desc}</Typography>
                                </GlassCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>

                {/* Free Resources Section */}
                <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: (theme) => alpha(theme.palette.mode === 'dark' ? '#000' : '#f5f5f5', 0.3) }}>
                    <Container maxWidth="md">
                        <Typography variant="h2" align="center" sx={{ mb: 2, color: 'text.primary', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                            More Than a{' '}
                            <Box component="span" sx={{ color: cyan }}>Reading App</Box>
                        </Typography>
                        <Typography variant="h6" align="center" sx={{ mb: 6, color: 'text.secondary', maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
                            We publish free resources because reading improvement should not be locked behind a paywall.
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <GlassCard sx={{ height: '100%', textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ fontWeight: 800, color: cyan, mb: 1 }}>100+</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Blog Guides</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Practical, no-fluff articles on speed reading, comprehension, focus, and retention.</Typography>
                                </GlassCard>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <GlassCard sx={{ height: '100%', textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ fontWeight: 800, color: green, mb: 1 }}>1</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Ebook + Companion Kit</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Read Faster Without Forgetting Everything, with a free 7-Day Reading Reset companion kit.</Typography>
                                </GlassCard>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <GlassCard sx={{ height: '100%', textAlign: 'center' }}>
                                    <Typography variant="h4" sx={{ fontWeight: 800, color: cyan, mb: 1 }}>Free</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Speed Test</Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Measure your reading speed and comprehension in under 2 minutes. No account required.</Typography>
                                </GlassCard>
                            </Grid>
                        </Grid>
                        <Box sx={{ textAlign: 'center', mt: 5, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button component={RouterLink} to="/blog" variant="outlined" color="primary" size="large" sx={{ py: 1.5, px: 4 }}>
                                Read the Blog
                            </Button>
                            <Button component={RouterLink} to="/ebook" variant="outlined" color="primary" size="large" sx={{ py: 1.5, px: 4 }}>
                                Get the Free Kit
                            </Button>
                            <Button component={RouterLink} to="/speed-test" variant="outlined" color="primary" size="large" sx={{ py: 1.5, px: 4 }}>
                                Take the Speed Test
                            </Button>
                        </Box>
                    </Container>
                </Box>

                {/* Company Info */}
                <Container maxWidth="sm" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
                        ReaderFlex is built by Emberline Ventures, LLC.
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 3 }}>
                        We are a small team that believes reading is one of the most rewarding things a person can do. Our goal is to help as many people as possible discover that for themselves.
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Questions? Email us at{' '}
                        <Box component="a" href="mailto:hello@readerflex.com" sx={{ color: cyan, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                            hello@readerflex.com
                        </Box>
                    </Typography>
                </Container>

                {/* Final CTA */}
                <Box
                    sx={{
                        py: { xs: 8, md: 12 },
                        position: 'relative',
                        textAlign: 'center',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0, left: '50%', transform: 'translateX(-50%)',
                            width: '80%', height: '1px',
                            background: `linear-gradient(90deg, transparent, ${alpha(cyan, 0.3)}, transparent)`,
                        },
                    }}
                >
                    <Container maxWidth="md">
                        {hasActiveAccess ? (
                            <SubscriberCtaContent
                                title="Stop fighting the page. Train the habit."
                                description="Share your affiliate link and earn $3.00/month when friends discover ReaderFlex."
                            />
                        ) : (
                            <>
                                <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, mb: 2, color: 'text.primary', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                                    Stop fighting the page.{' '}
                                    <Box component="span" sx={{ color: green }}>Train the habit.</Box>
                                </Typography>
                                <Typography variant="h6" sx={{ mb: 5, color: 'text.secondary', fontWeight: 400, maxWidth: 600, mx: 'auto' }}>
                                    Try ReaderFlex for $1. Full access for 3 days. Build a better reading rhythm with tools that actually help.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        size="large"
                                        onClick={() => navigate('/register')}
                                        sx={{ py: 1.5, px: 6, fontSize: '1.1rem' }}
                                    >
                                        Start Your Trial
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="large"
                                        onClick={() => navigate('/features')}
                                        endIcon={<ArrowForwardIcon />}
                                        sx={{ py: 1.5, px: 6 }}
                                    >
                                        Explore Features
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Container>
                </Box>

                <ScrollToTopButton />
            </Box>
        </>
    );
};

export default About;
